import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { insightsRequest, insightsError, downloadParticipationSummary } from 'Src/helpers/service/sltInsights';
import './ParticipationSummary.scss';
const labels = {
  pupilsPremium: 'Pupil Premium (PP)',
  specialEducationNeeds: 'SEND',
  freeSchoolMeals: 'Free School Meals (FSM)',
  ethnicityCode: 'Ethnicity'
};
const modes = {
  year: 'Same point last year',
  term: 'Previous teaching term',
  previous: 'Previous period',
  custom: 'Custom period',
  none: 'No comparison'
};
export function ParticipationSummary({
  user,
  query,
  data,
  compare = 'none',
  compareFrom,
  compareTo,
  comparisonError,
  reportKind = 'summary',
  selection = {}
}: {
  user: AppUser;
  query: any;
  data: any;
  compare?: string;
  compareFrom?: string;
  compareTo?: string;
  comparisonError?: string;
  reportKind?: 'summary' | 'clubs' | 'sport';
  selection?: any;
}) {
  const [available, setAvailable] = React.useState<string[]>([]),
    [selected, setSelected] = React.useState<string[]>([]);
  const [ready, setReady] = React.useState(false),
    [busy, setBusy] = React.useState(''),
    [error, setError] = React.useState('');
  const [namesAllowed, setNamesAllowed] = React.useState(false),
    [includeStudentNames, setNames] = React.useState(false);
  const [includeComparison, setComparison] = React.useState(compare !== 'none');
  React.useEffect(() => {
    let active = true;
    setReady(false);
    setNames(false);
    setNamesAllowed(false);
    setAvailable([]);
    setSelected([]);
    setError('');
    insightsRequest(user, 'participation-summary/options')
      .then(value => {
        if (active) {
          setAvailable(value.traitFields);
          setNamesAllowed(value.studentNamesAllowed === true);
          setReady(true);
        }
      })
      .catch(reason => {
        if (active) setError(insightsError(reason));
      });
    return () => {
      active = false;
    };
  }, [user.activeSchoolId, user.roleSessionKey, reportKind]);
  React.useEffect(() => setComparison(compare !== 'none'), [compare]);
  const formatDate = (date: any) =>
    new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: data.timezone || 'UTC'
    });
  const download = async (format: string) => {
    setBusy(format);
    setError('');
    try {
      await downloadParticipationSummary(
        user,
        { ...query, ...(reportKind !== 'summary' ? { area: reportKind } : {}) },
        {
          format,
          compare: includeComparison ? compare : 'none',
          traitFields: selected,
          ...(reportKind !== 'summary'
            ? { reportKind, selection, ...(includeStudentNames ? { includeStudentNames: true } : {}) }
            : {}),
          ...(includeComparison && compare === 'custom' ? { compareFrom, compareTo } : {})
        }
      );
    } catch (reason) {
      setError(insightsError(reason));
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="slps">
      <p>
        {reportKind === 'clubs'
          ? 'Club participation, the full club table, capacity, registers and comparison periods.'
          : reportKind === 'sport'
          ? 'School sport, event results, tournament progress and comparison periods.'
          : 'Ready to share: the current filters, participation figures, comparison and data-quality notes. No student names.'}
      </p>
      <dl className="slps-context">
        <div>
          <dt>Period</dt>
          <dd>
            {formatDate(data.period.from)} – {formatDate(+new Date(data.period.to) - 1)}
          </dd>
        </div>
        <div>
          <dt>Sources</dt>
          <dd>
            {
              { all: 'Clubs and sport', clubs: 'Club events', sport: 'Sport events' }[
                reportKind !== 'summary' ? reportKind : query.area || 'all'
              ]
            }
          </dd>
        </div>
        <div>
          <dt>Student population</dt>
          <dd>
            {data.population === 'archive'
              ? `Archived roster · ${formatDate(data.archive.observedAt)}`
              : data.population === 'historical'
              ? 'Verified historical membership'
              : 'Current student roster'}
            {data.archive?.excludedStudents > 0 && ` · incomplete: ${data.archive.excludedStudents} unmatched ${data.archive.excludedStudents === 1 ? 'pupil' : 'pupils'} excluded; counts cover matched pupils only`}
          </dd>
        </div>
        {query.years && (
          <div>
            <dt>Year groups</dt>
            <dd>
              {String(query.years)
                .split(',')
                .map(year => (year === 'unknown' ? 'Unknown year' : 'Y' + year))
                .join(', ')}
            </dd>
          </div>
        )}
        {(query.characteristic || query.characteristic2 || query.tableGroup) && (
          <div>
            <dt>Characteristics filter</dt>
            <dd>
              {[query.characteristic, query.characteristic2, query.tableGroup]
                .filter(Boolean)
                .map(v => {
                  const [field, ...rest] = v.split(':');
                  return `${labels[field] || field}: ${rest.join(':')}`;
                })
                .join(' · ')}
            </dd>
          </div>
        )}
        {query.tableYear && (
          <div>
            <dt>Table year</dt>
            <dd>Y{query.tableYear}</dd>
          </div>
        )}
        {query.segment && (
          <div>
            <dt>Student group</dt>
            <dd>
              {{
                notyet: 'Not yet taken part',
                ppnot: 'PP not reached',
                regular: 'Regular',
                occasional: 'Occasional',
                stopped: 'Stopped taking part',
                never: 'No recorded participation',
                retained: 'Retained participants',
                all: 'All students'
              }[query.segment] || query.segment}
            </dd>
          </div>
        )}
      </dl>
      {reportKind !== 'summary' && (
        <p className="sli-muted">
          Table filters:{' '}
          {reportKind === 'clubs'
            ? [
                selection.slotKey
                  ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][Number(selection.slotKey[0])] +
                    ' · ' +
                    ['Before school', 'Lunch', 'After school', 'Late'][Number(selection.slotKey[2])]
                  : null,
                selection.clubIds ? 'Selected activity / clubs' : null
              ]
                .filter(Boolean)
                .join(' · ') || 'All eligible clubs'
            : [selection.sportId ? 'Selected sport' : 'All sports', selection.resultType || 'ALL'].join(' · ')}
        </p>
      )}
      {reportKind === 'sport' && namesAllowed && (
        <label className="slps-option">
          <input type="checkbox" checked={includeStudentNames} onChange={e => setNames(e.target.checked)} />
          Include student names in results (student-access permission)
        </label>
      )}
      <h3>Comparison</h3>
      {compare !== 'none' ? (
        <label className="slps-option">
          <input type="checkbox" checked={includeComparison} onChange={e => setComparison(e.target.checked)} />
          Include {modes[compare] || compare}
        </label>
      ) : (
        <p className="sli-muted">No comparison selected. Choose one in the dashboard filters to include it.</p>
      )}
      {includeComparison && comparisonError && <p className="sli-error">Comparison unavailable: {comparisonError}</p>}
      <h3>Optional breakdowns</h3>
      <p className="sli-muted">Off by default. Only characteristics enabled for this school can be included.</p>
      {available.map(field => (
        <label className="slps-option" key={field}>
          <input
            type="checkbox"
            checked={selected.includes(field)}
            onChange={e => setSelected(e.target.checked ? [...selected, field] : selected.filter(f => f !== field))}
          />
          {labels[field]}
        </label>
      ))}
      {!ready && !error && <p role="status">Loading report options…</p>}
      <p className="slps-note">
        Both formats use the same data. Small groups are hidden; missing or unmeasured values remain unknown. Historical
        coverage is never silently replaced by current students.
      </p>
      {error && (
        <p role="alert" className="sli-error">
          {error}
        </p>
      )}
      <div className="slps-actions">
        <button
          className="sli-button sli-primary"
          disabled={!ready || !!busy || (includeComparison && !!comparisonError)}
          onClick={() => download('pdf')}
        >
          {busy === 'pdf' ? 'Generating PDF…' : 'Download PDF'}
        </button>
        <button
          className="sli-button"
          disabled={!ready || !!busy || (includeComparison && !!comparisonError)}
          onClick={() => download('xlsx')}
        >
          {busy === 'xlsx' ? 'Generating XLSX…' : 'Download XLSX'}
        </button>
      </div>
    </div>
  );
}
